const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

let isScriptLoaded = false;
let scriptLoadingPromise: Promise<void> | null = null;

/**
 * Dynamically injects the Google reCAPTCHA v3 script tag into the document head if not already loaded.
 */
export function loadRecaptchaScript(): Promise<void> {
  if (isScriptLoaded) return Promise.resolve();
  if (scriptLoadingPromise) return scriptLoadingPromise;

  if (!RECAPTCHA_SITE_KEY) {
    console.warn('VITE_RECAPTCHA_SITE_KEY is missing. reCAPTCHA script loading skipped.');
    return Promise.resolve();
  }

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      isScriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      scriptLoadingPromise = null;
      reject(new Error('Failed to load reCAPTCHA script.'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadingPromise;
}

/**
 * Executes a reCAPTCHA action and resolves with the generated token.
 * Returns an empty string if reCAPTCHA Site Key is not configured.
 */
export async function executeRecaptcha(action: string): Promise<string> {
  if (!RECAPTCHA_SITE_KEY) {
    return '';
  }

  await loadRecaptchaScript();

  return new Promise<string>((resolve, reject) => {
    const grecaptcha = (window as any).grecaptcha;
    if (!grecaptcha) {
      reject(new Error('reCAPTCHA client library not initialized.'));
      return;
    }

    grecaptcha.ready(() => {
      grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action })
        .then((token: string) => resolve(token))
        .catch((err: any) => reject(err));
    });
  });
}
